import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useAuth } from '../auth/AuthContext';
import { wishlistApi, WishlistItem } from '../api/wishlist';
import { productApi } from '../api/product';

interface WishlistContextType {
  wishlistItems: WishlistItem[];
  wishlistProductIds: Set<number>;
  isLoading: boolean;
  refreshWishlist: () => Promise<void>;
  addToWishlist: (productId: number) => Promise<WishlistItem | null>;
  removeFromWishlist: (wishlistId: number) => Promise<boolean>;
  isInWishlist: (productId: number) => boolean;
  getWishlistId: (productId: number) => number | undefined;
  refreshProductDetails: (productId: number) => Promise<void>;
}

const WishlistContext = createContext<WishlistContextType | undefined>(undefined);

interface WishlistProviderProps {
  children: ReactNode;
}

export const WishlistProvider: React.FC<WishlistProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [wishlistItems, setWishlistItems] = useState<WishlistItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Create a Set of product IDs for fast lookup
  const wishlistProductIds = new Set(wishlistItems.map(item => item.productId));

  // Fetch wishlist when user logs in
  useEffect(() => {
    if (isAuthenticated && user) {
      refreshWishlist();
    } else {
      // Clear wishlist when user logs out
      setWishlistItems([]);
    }
  }, [isAuthenticated, user]);

  const refreshWishlist = async () => {
    if (!isAuthenticated) return;

    setIsLoading(true);
    try {
      const items = await wishlistApi.getWishlist();
      
      // Fetch product details for each wishlist item
      const itemsWithProducts = await Promise.all(
        items.map(async (item) => {
          try {
            const product = await productApi.getProductById(item.productId);
            return {
              ...item,
              product: {
                id: product.id,
                name: product.name,
                description: product.description,
                imageUrl: product.imageUrl,
                price: product.price,
              },
            };
          } catch (error) {
            console.error(`Error fetching product ${item.productId}:`, error);
            // Return item without product details if fetch fails
            return item;
          }
        })
      );
      
      setWishlistItems(itemsWithProducts);
    } catch (error) {
      console.error('Error fetching wishlist:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const addToWishlist = async (productId: number): Promise<WishlistItem | null> => {
    if (!isAuthenticated) return null;

    try {
      const response = await wishlistApi.addToWishlist(productId);
      if (response.success && response.item) {
        // Fetch product details for the new wishlist item
        try {
          const product = await productApi.getProductById(productId);
          const itemWithProduct = {
            ...response.item,
            product: {
              id: product.id,
              name: product.name,
              description: product.description,
              imageUrl: product.imageUrl,
              price: product.price,
            },
          };
          setWishlistItems(prev => [...prev, itemWithProduct]);
          return itemWithProduct;
        } catch (error) {
          console.error(`Error fetching product ${productId}:`, error);
          // Add item without product details if fetch fails
          setWishlistItems(prev => [...prev, response.item]);
          return response.item;
        }
      }
      return null;
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      return null;
    }
  };

  const removeFromWishlist = async (wishlistId: number): Promise<boolean> => {
    if (!isAuthenticated) return false;

    try {
      const response = await wishlistApi.removeFromWishlist(wishlistId);
      if (response.success) {
        setWishlistItems(prev => prev.filter(item => item.id !== wishlistId));
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error removing from wishlist:', error);
      return false;
    }
  };

  const isInWishlist = (productId: number): boolean => {
    return wishlistProductIds.has(productId);
  };

  const getWishlistId = (productId: number): number | undefined => {
    const item = wishlistItems.find(item => item.productId === productId);
    return item?.id;
  };

  const refreshProductDetails = async (productId: number) => {
    try {
      const product = await productApi.getProductById(productId);
      setWishlistItems(prev => 
        prev.map(item => 
          item.productId === productId 
            ? {
                ...item,
                product: {
                  id: product.id,
                  name: product.name,
                  description: product.description,
                  imageUrl: product.imageUrl,
                  price: product.price,
                },
              }
            : item
        )
      );
    } catch (error) {
      console.error(`Error refreshing product ${productId}:`, error);
    }
  };

  const value: WishlistContextType = {
    wishlistItems,
    wishlistProductIds,
    isLoading,
    refreshWishlist,
    addToWishlist,
    removeFromWishlist,
    isInWishlist,
    getWishlistId,
    refreshProductDetails,
  };

  return (
    <WishlistContext.Provider value={value}>
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = (): WishlistContextType => {
  const context = useContext(WishlistContext);
  if (context === undefined) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
};
